import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import Layout from "@/components/Layout";

import Login from "@/pages/SocietyLogin";
import SuperAdminLogin from "@/pages/SuperAdminLogin";
import Societies from "@/pages/super/Societies";
import SuperDashboard from "@/pages/super/SuperDashboard";
import SuperWhatsApp from "@/pages/super/SuperWhatsApp";
import SuperReports from "@/pages/super/SuperReports";
import Dashboard from "@/pages/society/Dashboard";
import Tickets from "@/pages/society/Tickets";
import Vendors from "@/pages/society/Vendors";
import Residents from "@/pages/society/Residents";
import SuperAIActivity from "@/pages/super/SuperAIActivity";
import Notifications from "@/pages/society/Notifications";
import Assets from "@/pages/society/Assets";
import Escalated from "@/pages/society/Escalated";
import Committee from "@/pages/society/Committee";
import AssignmentLogic from "@/pages/society/AssignmentLogic";
import Suggestions from "@/pages/society/Suggestions";
import AuditLog from "@/pages/society/AuditLog";
import Reports from "@/pages/society/Reports";
import ChangePassword from "@/pages/society/ChangePassword";
import Join from "@/pages/public/Join";
import TicketView from "@/pages/public/TicketView";
import VendorTicketView from "@/pages/public/VendorTicketView";
import DeveloperLogin from "@/pages/DeveloperLogin";
import Developers from "@/pages/super/Developers";
import DevSocieties from "@/pages/developer/DevSocieties";

const HOME = { super_admin: "/super/dashboard", developer: "/developer/societies" };

// roles: "super" = super admin only, "dev" = developer only, default = society screens
// (society admin, or super admin / developer working inside a society).
function Protected({ children, roles }) {
  const { user, scopeSociety } = useAuth();
  const loc = useLocation();
  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    );
  if (!user) return <Navigate to={loc.pathname.startsWith("/developer") ? "/developer/login" : "/login"} replace />;
  if (user.must_change_password && loc.pathname !== "/change-password") return <Navigate to="/change-password" replace />;
  const home = HOME[user.role] || "/dashboard";
  if (roles === "super" && user.role !== "super_admin") return <Navigate to={home} replace />;
  if (roles === "dev" && user.role !== "developer") return <Navigate to={home} replace />;
  if (!roles && loc.pathname !== "/change-password" && user.role !== "society_admin" && !scopeSociety) return <Navigate to={home} replace />;
  return <Layout>{children}</Layout>;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={HOME[user.role] || "/dashboard"} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/admin/login" element={<SuperAdminLogin />} />
            <Route path="/developer/login" element={<DeveloperLogin />} />
            <Route path="/developer/societies" element={<Protected roles="dev"><DevSocieties /></Protected>} />
            <Route path="/super/developers" element={<Protected roles="super"><Developers /></Protected>} />
            <Route path="/join/:token" element={<Join />} />
            <Route path="/t/:token" element={<TicketView />} />
            <Route path="/v/:token" element={<VendorTicketView />} />
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/super/dashboard" element={<Protected roles="super"><SuperDashboard /></Protected>} />
            <Route path="/super/societies" element={<Protected roles="super"><Societies /></Protected>} />
            <Route path="/super/whatsapp" element={<Protected roles="super"><SuperWhatsApp /></Protected>} />
            <Route path="/super/reports" element={<Protected roles="super"><SuperReports /></Protected>} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/tickets" element={<Protected><Tickets /></Protected>} />
            <Route path="/vendors" element={<Protected><Vendors /></Protected>} />
            <Route path="/residents" element={<Protected><Residents /></Protected>} />
            <Route path="/super/ai-activity" element={<Protected roles="super"><SuperAIActivity /></Protected>} />
            <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
            <Route path="/assets" element={<Protected><Assets /></Protected>} />
            <Route path="/escalated" element={<Protected><Escalated /></Protected>} />
            <Route path="/committee" element={<Protected><Committee /></Protected>} />
            <Route path="/assignment-logic" element={<Protected><AssignmentLogic /></Protected>} />
            <Route path="/suggestions" element={<Protected><Suggestions /></Protected>} />
            {/* Audit Log hidden from society admins (kept in code): <Route path="/audit" element={<Protected><AuditLog /></Protected>} /> */}
            <Route path="/audit" element={<Navigate to="/dashboard" replace />} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
